import React from 'react'
import { Button, Text } from '@retailer-platform/shared-components/src/tds'
import { SparkleIcon } from '@instacart/ids-core'
import { useTheme } from '@retailer-platform/shared-components/src/tds'
import styled from '@emotion/styled'
import { useDashMessages } from '../../../intl/intl.hooks'
import AiGradients from './AiGradients'

interface AiButtonCtaModalProps {
  isOpen: boolean
  onClose: () => void
  onTryIt: () => void
}

const OverlayContainer = styled.div<{ isOpen: boolean }>(({ isOpen }) => ({
  position: 'fixed',
  bottom: '80px',
  right: '20px',
  width: '330px',
  maxHeight: '500px',
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 99,
  display: isOpen ? 'flex' : 'none',
  flexDirection: 'column',
  overflow: 'hidden',
}))

const ContentSection = styled.div({
  flex: 1,
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
})

const IconSection = styled.div({
  marginBottom: '16px',
  display: 'flex',
  justifyContent: 'flex-start',
})

const TextSection = styled.div({
  marginBottom: '16px',
})

const ButtonGroup = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '16px',
})

const GradientButton = styled.button({
  background: 'linear-gradient(75deg, var(--Mint, #22BF90) 33.32%, #2B9BD8 94.98%)',
  border: 'none',
  borderRadius: '8px',
  color: 'white',
  padding: '12px 16px',
  fontSize: '14px',
  fontWeight: '600',
  fontFamily: 'inherit',
  cursor: 'pointer',
  width: '96px',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.9,
  },
  '&:active': {
    opacity: 0.8,
  },
  '&:focus': {
    outline: '2px solid #2B9BD8',
    outlineOffset: '2px',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
})

export const AiButtonCtaModal: React.FC<AiButtonCtaModalProps> = ({ isOpen, onClose, onTryIt }) => {
  const theme = useTheme()
  const messages = useDashMessages({
    title: 'aiButton.ctaModal.title',
    description: 'aiButton.ctaModal.description',
    notNowButton: 'aiButton.ctaModal.button.notNow',
    tryItButton: 'aiButton.ctaModal.button.tryIt',
  })

  const handleTryIt = () => {
    onTryIt()
    onClose()
  }

  return (
    <OverlayContainer isOpen={isOpen}>
      <ContentSection>
        <IconSection>
          <AiGradients />
          <SparkleIcon width={24} height={24} fill="url(#sparkle-gradient-blue)" />
        </IconSection>
        <TextSection>
          <Text typography="subtitle" style={{ marginBottom: '8px' }}>
            {messages.title}
          </Text>
          <Text typography="bodyRegular" style={{ color: theme.colors.systemGrayscale60 }}>
            {messages.description}
          </Text>
        </TextSection>
        <ButtonGroup>
          <Button variant="tertiary" onClick={onClose}>
            {messages.notNowButton}
          </Button>
          <GradientButton onClick={handleTryIt}>{messages.tryItButton}</GradientButton>
        </ButtonGroup>
      </ContentSection>
    </OverlayContainer>
  )
}

export default AiButtonCtaModal
